<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import ChatlottaIndustryTemplatesAPI from 'dashboard/api/chatlotta/industryTemplates';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ChatlottaBusinessTypePicker from '../../onboarding/ChatlottaBusinessTypePicker.vue';
import ChatlottaCustomQuestionsEditor from '../../onboarding/ChatlottaCustomQuestionsEditor.vue';
import ChatlottaAnswerPreview from './ChatlottaAnswerPreview.vue';
import ChatlottaBookingRequests from './ChatlottaBookingRequests.vue';
import { INDUSTRY_OPTIONS } from '../../onboarding/constants';

const UI_LABELS = {
  title: 'Chatlotta setup',
  progress: 'Steg',
  locked: 'Låst',
  complete: 'Klar',
  continue: 'Fortsätt',
  skip: 'Hoppa över',
  back: 'Tillbaka',
  activate: 'Spara och aktivera',
  active: 'Profil aktiv',
  success: 'Chatlotta-profilen sparades.',
  error: 'Det gick inte att spara Chatlotta-profilen.',
  businessType: 'Verksamhetstyp',
  businessName: 'Verksamhetens namn',
  channels: 'Kanaler',
  openingHours: 'Öppettider',
  address: 'Adress',
  phone: 'Telefon',
  email: 'E-post',
  website: 'Webbplats',
  services: 'Tjänster, produkter och priser',
  prices: 'Priser eller prisexempel',
  fallback: 'Fallback-meddelande',
  requiredHint: 'Fyll i det här steget för att fortsätta.',
  industryChangeTitle: 'Byta verksamhetstyp?',
  industryChangeHelp:
    'Du har redan fyllt i branschspecifika svar. Välj om de ska behållas eller rensas.',
  keepAnswers: 'Behåll svar',
  resetAnswers: 'Rensa branschsvar',
  suggestedQuestions: 'Förslag för vald verksamhet',
  example: 'Exempel:',
  channelHelp: 'Välj en eller flera kanaler. Du kan ändra detta senare.',
  readyToTest: 'Redo att testa',
  missing: 'Saknas',
};

const CHANNEL_OPTIONS = [
  { value: 'Website', label: 'Webbplats' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Telegram', label: 'Telegram' },
  { value: 'Email', label: 'E-post' },
];

const STEPS = [
  {
    id: 'business',
    title: 'Verksamhet',
    description: 'Välj vilken typ av verksamhet du driver.',
  },
  {
    id: 'channels',
    title: 'Kanaler',
    description: 'Välj var kunder ska kunna kontakta dig.',
  },
  {
    id: 'company',
    title: 'Företagsinfo',
    description: 'Lägg in grunduppgifter som kunder ofta frågar efter.',
  },
  {
    id: 'services',
    title: 'Öppettider och tjänster',
    description: 'Berätta när ni har öppet och vad ni erbjuder.',
  },
  {
    id: 'booking',
    title: 'Bokning',
    description: 'Välj om Chatlotta ska samla in bokningsförfrågningar.',
    optional: true,
  },
  {
    id: 'questions',
    title: 'Egna frågor och svar',
    description: 'Lägg till extra svar som passar just ditt företag.',
    optional: true,
  },
  {
    id: 'preview',
    title: 'Förhandsvisning',
    description: 'Se vad Chatlotta kan svara och aktivera profilen.',
  },
];

const { currentAccount, finishOnboarding } = useAccount();
const store = useStore();

const industryTemplates = ref(null);
const isLoadingIndustryTemplates = ref(false);
const industryTemplatesFailed = ref(false);
const isSaving = ref(false);
const hasHydratedProfile = ref(false);
const activeStep = ref(0);
const maxUnlockedStep = ref(0);
const industry = ref('');
const pendingIndustry = ref('');
const profile = ref({
  setup_step: 0,
  is_active: false,
  industry_id: '',
  business_name: '',
  channels: [],
  website: '',
  opening_hours: '',
  address: '',
  phone: '',
  email: '',
  services_products: '',
  prices: '',
  fallback_message: '',
  booking: {
    enabled: false,
    available_days: '',
    available_times: '',
    instructions: '',
    requests: [],
  },
  custom_questions: [],
});

const currentStep = computed(() => STEPS[activeStep.value]);
const progressLabel = computed(
  () => `${UI_LABELS.progress} ${activeStep.value + 1} av ${STEPS.length}`
);

const customIndustryLabel = computed(() => {
  if (!industry.value.startsWith('custom:')) return '';
  return industry.value.replace('custom:', '').trim();
});

const selectedIndustry = computed(
  () => industryTemplates.value?.industries?.[industry.value] || null
);
const industryHints = computed(() => selectedIndustry.value?.setup_hints || {});
const serviceQuestions = computed(() =>
  (selectedIndustry.value?.questions || [])
    .filter(question =>
      ['hours', 'services', 'menu', 'products', 'prices'].includes(
        question.category
      )
    )
    .slice(0, 4)
);
const bookingQuestions = computed(() =>
  (selectedIndustry.value?.questions || [])
    .filter(question => question.category === 'booking')
    .slice(0, 4)
);

const hasIndustrySpecificAnswers = computed(() =>
  Boolean(
    profile.value.services_products ||
      profile.value.prices ||
      profile.value.booking?.available_days ||
      profile.value.booking?.available_times ||
      profile.value.booking?.instructions
  )
);

const isLastStep = computed(() => activeStep.value === STEPS.length - 1);
const canGoBack = computed(() => activeStep.value > 0);

const canContinue = computed(() => {
  switch (currentStep.value.id) {
    case 'business':
      return Boolean(industry.value);
    case 'channels':
      return Boolean(profile.value.channels?.length);
    case 'company':
      return Boolean(
        profile.value.business_name &&
          (profile.value.phone || profile.value.email || profile.value.website)
      );
    case 'services':
      return Boolean(
        profile.value.opening_hours || profile.value.services_products
      );
    default:
      return true;
  }
});

const selectedChannelsText = computed(() => {
  if (!profile.value.channels?.length) return 'Inga kanaler valda än.';
  return profile.value.channels
    .map(
      channel =>
        CHANNEL_OPTIONS.find(option => option.value === channel)?.label ||
        channel
    )
    .join(', ');
});

const readinessItems = computed(() => [
  {
    id: 'business',
    label: 'Verksamhet vald',
    complete: Boolean(industry.value),
  },
  {
    id: 'channels',
    label: 'Minst en kanal vald',
    complete: Boolean(profile.value.channels?.length),
  },
  {
    id: 'company',
    label: 'Företagsnamn och kontakt finns',
    complete: Boolean(
      profile.value.business_name &&
        (profile.value.phone || profile.value.email || profile.value.website)
    ),
  },
  {
    id: 'services',
    label: 'Öppettider eller tjänster finns',
    complete: Boolean(
      profile.value.opening_hours || profile.value.services_products
    ),
  },
  {
    id: 'fallback',
    label: 'Fallback för okända frågor finns',
    complete: Boolean(profile.value.fallback_message),
  },
]);

const profilePayload = computed(() => ({
  ...profile.value,
  industry_id: industry.value,
  setup_step: maxUnlockedStep.value,
  industry_custom_label: customIndustryLabel.value,
  channels: profile.value.channels || [],
  custom_questions: profile.value.custom_questions || [],
  booking: {
    enabled: profile.value.booking?.enabled || false,
    available_days: profile.value.booking?.available_days || '',
    available_times: profile.value.booking?.available_times || '',
    instructions: profile.value.booking?.instructions || '',
    requests: profile.value.booking?.requests || [],
  },
}));

const clampStep = step =>
  Math.max(0, Math.min(Number(step) || 0, STEPS.length - 1));

const populateProfile = () => {
  if (!currentAccount.value) return;

  const attrs = currentAccount.value?.custom_attributes || {};
  const savedProfile = attrs.chatlotta_business_profile || {};
  const savedStep = clampStep(savedProfile.setup_step);

  industry.value = attrs.industry || '';
  maxUnlockedStep.value = Math.max(maxUnlockedStep.value, savedStep);
  if (!hasHydratedProfile.value) {
    activeStep.value = savedStep;
    hasHydratedProfile.value = true;
  }
  profile.value = {
    setup_step: savedStep,
    is_active: savedProfile.is_active || false,
    industry_id: savedProfile.industry_id || attrs.industry || '',
    business_name:
      savedProfile.business_name || currentAccount.value?.name || '',
    channels: savedProfile.channels || [],
    website: savedProfile.website || attrs.website || '',
    opening_hours: savedProfile.opening_hours || '',
    address: savedProfile.address || '',
    phone: savedProfile.phone || '',
    email: savedProfile.email || '',
    services_products: savedProfile.services_products || '',
    prices: savedProfile.prices || '',
    fallback_message: savedProfile.fallback_message || '',
    booking: {
      enabled: savedProfile.booking?.enabled || false,
      available_days: savedProfile.booking?.available_days || '',
      available_times: savedProfile.booking?.available_times || '',
      instructions: savedProfile.booking?.instructions || '',
      requests: savedProfile.booking?.requests || [],
    },
    custom_questions: savedProfile.custom_questions || [],
  };
};

const fetchIndustryTemplates = async () => {
  isLoadingIndustryTemplates.value = true;
  industryTemplatesFailed.value = false;

  try {
    const response = await ChatlottaIndustryTemplatesAPI.getTemplates();
    industryTemplates.value = response.data?.payload || null;
  } catch {
    industryTemplatesFailed.value = true;
    industryTemplates.value = null;
  } finally {
    isLoadingIndustryTemplates.value = false;
  }
};

const toggleChannel = channel => {
  const channels = profile.value.channels || [];
  profile.value.channels = channels.includes(channel)
    ? channels.filter(item => item !== channel)
    : [...channels, channel];
};

const applyIndustry = ({ industryId, resetAnswers = false }) => {
  industry.value = industryId;
  profile.value.industry_id = industryId;

  if (resetAnswers) {
    profile.value.services_products = '';
    profile.value.prices = '';
    profile.value.booking = {
      ...profile.value.booking,
      enabled: false,
      available_days: '',
      available_times: '',
      instructions: '',
    };
  }

  pendingIndustry.value = '';
};

const requestIndustryChange = industryId => {
  if (!industry.value || industry.value === industryId) {
    applyIndustry({ industryId });
    return;
  }

  if (hasIndustrySpecificAnswers.value) {
    pendingIndustry.value = industryId;
    return;
  }

  applyIndustry({ industryId });
};

const goToStep = index => {
  if (index > maxUnlockedStep.value) return;
  activeStep.value = index;
};

const saveProfile = async ({
  nextStep = activeStep.value,
  showSuccess = true,
} = {}) => {
  isSaving.value = true;
  const unlockedStep = clampStep(nextStep);
  const nextUnlockedStep = Math.max(maxUnlockedStep.value, unlockedStep);

  try {
    await finishOnboarding({
      website: profile.value.website,
      industry: industry.value,
      chatlotta_business_profile: {
        ...profilePayload.value,
        setup_step: nextUnlockedStep,
      },
    });
    maxUnlockedStep.value = nextUnlockedStep;
    if (showSuccess) useAlert(UI_LABELS.success);
    return true;
  } catch {
    useAlert(UI_LABELS.error);
    return false;
  } finally {
    isSaving.value = false;
  }
};

const continueStep = async () => {
  if (!canContinue.value) return;

  const nextStep = clampStep(activeStep.value + 1);
  const didSave = await saveProfile({ nextStep, showSuccess: false });
  if (didSave) activeStep.value = nextStep;
};

const skipStep = async () => {
  await continueStep();
};

const goBack = () => {
  activeStep.value = clampStep(activeStep.value - 1);
};

const activateProfile = async () => {
  const previousActiveState = profile.value.is_active;
  profile.value.is_active = true;
  const didSave = await saveProfile({
    nextStep: STEPS.length - 1,
    showSuccess: true,
  });
  if (didSave) {
    activeStep.value = STEPS.length - 1;
  } else {
    profile.value.is_active = previousActiveState;
  }
};

const handleSubmit = () => {
  if (isLastStep.value) {
    activateProfile();
    return;
  }

  continueStep();
};

onMounted(async () => {
  await store.dispatch('accounts/get');
  populateProfile();
  fetchIndustryTemplates();
});

watch(
  () => currentAccount.value?.custom_attributes,
  () => populateProfile()
);
</script>

<template>
  <div class="flex w-full max-w-5xl flex-col ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="UI_LABELS.title" />
    <SettingsLayout class="mt-3">
      <template #body>
        <form class="grid gap-5" @submit.prevent="handleSubmit">
          <div class="grid gap-2">
            <div class="flex items-center justify-between gap-3">
              <span class="text-sm font-medium text-n-slate-11">
                {{ progressLabel }}
              </span>
              <span class="text-sm text-n-slate-10">
                {{ currentStep.description }}
              </span>
            </div>
            <div class="h-2 overflow-hidden rounded-md bg-n-alpha-2">
              <div
                class="h-full rounded-md bg-woot-500 transition-all"
                :style="{
                  width: `${((activeStep + 1) / STEPS.length) * 100}%`,
                }"
              />
            </div>
          </div>

          <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-7">
            <button
              v-for="(step, index) in STEPS"
              :key="step.id"
              type="button"
              class="grid min-h-20 gap-1 rounded-md border p-3 text-left transition"
              :class="{
                'border-woot-500 bg-woot-50 text-n-slate-12':
                  index === activeStep,
                'border-n-weak bg-n-alpha-1 text-n-slate-11':
                  index !== activeStep && index <= maxUnlockedStep,
                'border-n-weak bg-n-alpha-2 text-n-slate-9':
                  index > maxUnlockedStep,
              }"
              :disabled="index > maxUnlockedStep"
              @click="goToStep(index)"
            >
              <span class="text-xs font-medium">
                {{ UI_LABELS.progress }} {{ index + 1 }}
              </span>
              <span class="text-sm font-medium">{{ step.title }}</span>
              <span
                v-if="index > maxUnlockedStep"
                class="text-xs text-n-slate-9"
              >
                {{ UI_LABELS.locked }}
              </span>
              <span
                v-else-if="index < maxUnlockedStep"
                class="text-xs text-n-slate-9"
              >
                {{ UI_LABELS.complete }}
              </span>
            </button>
          </div>

          <section
            class="grid gap-5 rounded-md border border-n-weak bg-n-alpha-1 p-5"
          >
            <div class="grid gap-1">
              <span class="text-sm font-medium text-woot-600">
                {{ progressLabel }}
              </span>
              <h3 class="mb-0 text-xl font-semibold text-n-slate-12">
                {{ currentStep.title }}
              </h3>
              <p class="mb-0 text-sm text-n-slate-10">
                {{ currentStep.description }}
              </p>
            </div>

            <div v-if="currentStep.id === 'business'" class="grid gap-4">
              <label class="grid gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ UI_LABELS.businessType }}
                </span>
                <ChatlottaBusinessTypePicker
                  :model-value="industry"
                  :templates="industryTemplates"
                  :fallback-options="INDUSTRY_OPTIONS"
                  :is-loading="isLoadingIndustryTemplates"
                  :has-error="industryTemplatesFailed"
                  @update:model-value="requestIndustryChange"
                />
              </label>
              <div
                v-if="pendingIndustry"
                class="grid gap-3 rounded-md border border-n-amber-5 bg-n-amber-2 p-4"
              >
                <div class="grid gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ UI_LABELS.industryChangeTitle }}
                  </span>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ UI_LABELS.industryChangeHelp }}
                  </p>
                </div>
                <div class="flex flex-wrap justify-end gap-2">
                  <NextButton
                    :label="UI_LABELS.keepAnswers"
                    slate
                    faded
                    @click.prevent="
                      applyIndustry({ industryId: pendingIndustry })
                    "
                  />
                  <NextButton
                    :label="UI_LABELS.resetAnswers"
                    amber
                    solid
                    @click.prevent="
                      applyIndustry({
                        industryId: pendingIndustry,
                        resetAnswers: true,
                      })
                    "
                  />
                </div>
              </div>
            </div>

            <div v-else-if="currentStep.id === 'channels'" class="grid gap-4">
              <div class="grid gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ UI_LABELS.channels }}
                </span>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{ UI_LABELS.channelHelp }}
                </p>
              </div>
              <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <label
                  v-for="channel in CHANNEL_OPTIONS"
                  :key="channel.value"
                  class="flex items-center gap-3 rounded-md border border-n-weak bg-n-alpha-1 px-4 py-3 text-sm text-n-slate-11"
                >
                  <input
                    type="checkbox"
                    class="size-4"
                    :checked="profile.channels.includes(channel.value)"
                    @change="toggleChannel(channel.value)"
                  />
                  <span>{{ channel.label }}</span>
                </label>
              </div>
              <p
                class="mb-0 rounded-md bg-n-alpha-2 p-3 text-sm text-n-slate-11"
              >
                {{ selectedChannelsText }}
              </p>
            </div>

            <div v-else-if="currentStep.id === 'company'" class="grid gap-4">
              <Input
                v-model="profile.business_name"
                :label="UI_LABELS.businessName"
                size="sm"
              />
              <Input
                v-model="profile.address"
                :label="UI_LABELS.address"
                size="sm"
              />
              <div class="grid gap-4 sm:grid-cols-2">
                <Input
                  v-model="profile.phone"
                  :label="UI_LABELS.phone"
                  size="sm"
                />
                <Input
                  v-model="profile.email"
                  :label="UI_LABELS.email"
                  size="sm"
                />
              </div>
              <Input
                v-model="profile.website"
                :label="UI_LABELS.website"
                size="sm"
              />
            </div>

            <div v-else-if="currentStep.id === 'services'" class="grid gap-4">
              <section
                v-if="serviceQuestions.length"
                class="grid gap-2 rounded-md bg-n-alpha-2 p-4"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ UI_LABELS.suggestedQuestions }}
                </span>
                <div class="grid gap-2 sm:grid-cols-2">
                  <div
                    v-for="question in serviceQuestions"
                    :key="question.id"
                    class="text-sm text-n-slate-11"
                  >
                    <div class="font-medium text-n-slate-12">
                      {{ question.label_sv }}
                    </div>
                    <div v-if="question.example_sv" class="text-n-slate-10">
                      {{ UI_LABELS.example }} {{ question.example_sv }}
                    </div>
                  </div>
                </div>
              </section>
              <Input
                v-model="profile.opening_hours"
                :label="UI_LABELS.openingHours"
                :placeholder="
                  serviceQuestions.find(item => item.category === 'hours')
                    ?.example_sv || ''
                "
                size="sm"
              />
              <Textarea
                v-model="profile.services_products"
                :label="industryHints.services_label_sv || UI_LABELS.services"
                :placeholder="industryHints.services_placeholder_sv || ''"
                :max-length="2000"
                resize
              />
              <Textarea
                v-model="profile.prices"
                :label="industryHints.prices_label_sv || UI_LABELS.prices"
                :placeholder="industryHints.prices_placeholder_sv || ''"
                :max-length="1000"
                resize
              />
              <Textarea
                v-model="profile.fallback_message"
                :label="UI_LABELS.fallback"
                :max-length="1000"
                resize
              />
            </div>

            <div v-else-if="currentStep.id === 'booking'" class="grid gap-4">
              <ChatlottaBookingRequests
                v-model="profile.booking"
                :industry-hints="industryHints"
                :suggested-questions="bookingQuestions"
                setup-mode
              />
            </div>

            <div v-else-if="currentStep.id === 'questions'" class="grid gap-4">
              <ChatlottaCustomQuestionsEditor
                v-model="profile.custom_questions"
                :industry-id="industry"
              />
            </div>

            <div v-else class="grid gap-4">
              <ChatlottaAnswerPreview
                :profile="profile"
                :industry="selectedIndustry"
                :industry-hints="industryHints"
              />
              <section class="grid gap-3 rounded-md border border-n-weak p-4">
                <h4 class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ UI_LABELS.readyToTest }}
                </h4>
                <div class="grid gap-2">
                  <div
                    v-for="item in readinessItems"
                    :key="item.id"
                    class="flex items-center justify-between rounded-md bg-n-alpha-2 px-3 py-2 text-sm"
                  >
                    <span class="text-n-slate-11">{{ item.label }}</span>
                    <span
                      class="font-medium"
                      :class="
                        item.complete ? 'text-green-600' : 'text-n-slate-9'
                      "
                    >
                      {{
                        item.complete ? UI_LABELS.complete : UI_LABELS.missing
                      }}
                    </span>
                  </div>
                </div>
              </section>
            </div>
          </section>

          <div class="flex flex-wrap items-center justify-between gap-3">
            <NextButton
              :label="UI_LABELS.back"
              :disabled="!canGoBack || isSaving"
              slate
              faded
              @click.prevent="goBack"
            />
            <div class="flex flex-wrap justify-end gap-2">
              <span
                v-if="!canContinue && !currentStep.optional"
                class="self-center text-sm text-n-slate-10"
              >
                {{ UI_LABELS.requiredHint }}
              </span>
              <NextButton
                v-if="currentStep.optional"
                :label="UI_LABELS.skip"
                :disabled="isSaving"
                slate
                faded
                @click.prevent="skipStep"
              />
              <NextButton
                v-if="!isLastStep"
                :label="UI_LABELS.continue"
                :disabled="!canContinue || isSaving"
                :is-loading="isSaving"
                blue
                solid
                @click.prevent="continueStep"
              />
              <NextButton
                v-else
                type="submit"
                :label="
                  profile.is_active ? UI_LABELS.active : UI_LABELS.activate
                "
                :is-loading="isSaving"
                blue
                solid
              />
            </div>
          </div>
        </form>
      </template>
    </SettingsLayout>
  </div>
</template>
